// Invoice template management for Support-Workflow (CODA-2626).
// Uses /api/automatic-support-workflow/invoice-templates.

import { useState, useEffect } from 'react'
import { Copy, Info, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Switch,
  Textarea,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import type { SupportWorkflowInvoiceTemplate, AswInvoiceTemplateType } from '@shared/types'

// ─── Variables per template type ─────────────────────────────────────────────

const TEMPLATE_TYPE_META: Record<
  AswInvoiceTemplateType,
  { label: string; description: string; variables: { key: string; description: string }[] }
> = {
  ticket: {
    label: 'Servicebericht',
    description: 'Ticket-Textzeile — vollständiger Servicebericht mit Problemschilderung und Leistungsdetails',
    variables: [
      { key: '%%headline-problem%%', description: 'Überschrift Problembeschreibung' },
      { key: '%%text-problem%%', description: 'Problembeschreibung' },
      { key: '%%headline-work-done%%', description: 'Überschrift Durchgeführte Arbeiten' },
      { key: '%%text-work-done%%', description: 'Durchgeführte Arbeiten' },
      { key: '%%headline-to-do%%', description: 'Überschrift Offene Punkte' },
      { key: '%%text-to-do%%', description: 'Offene Punkte' },
      { key: '%%place%%', description: 'Standort' },
      { key: '%%client%%', description: 'Auftraggeber' },
      { key: '%%dateString%%', description: 'Leistungszeitraum (formatiert)' },
      { key: '%%timestring%%', description: 'Arbeitszeit (h)' },
      { key: '%%timestringNotBilled%%', description: 'Nicht berechnete Zeit (h)' },
      { key: '%%distanceDriven%%', description: 'Gefahrene Kilometer' },
      { key: '%%techniciansKeys%%', description: 'Techniker-Kürzel' },
      { key: '%%detailListTechnician%%', description: 'Detailliste Techniker (Repeater)' },
      { key: '%%supportId%%', description: 'Ticket-ID' },
    ],
  },
  service: {
    label: 'Servicetextzeile',
    description: 'Service-Textzeile — kompakte Zusammenfassung von Leistungszeit und Fahrt',
    variables: [
      { key: '%%client%%', description: 'Auftraggeber' },
      { key: '%%dateString%%', description: 'Leistungszeitraum (formatiert)' },
      { key: '%%timestring%%', description: 'Arbeitszeit (h)' },
      { key: '%%timestringNotBilled%%', description: 'Nicht berechnete Zeit' },
      { key: '%%distanceDriven%%', description: 'Gefahrene Kilometer' },
      { key: '%%techniciansKeys%%', description: 'Techniker-Kürzel' },
      { key: '%%supportId%%', description: 'Ticket-ID' },
    ],
  },
  ware: {
    label: 'Warentextzeile',
    description: 'Waren-Textzeile — Artikelinformationen für gelieferte Waren',
    variables: [
      { key: '%%supportId%%', description: 'Ticket-ID (Referenz)' },
      { key: '%%client%%', description: 'Auftraggeber' },
      { key: '%%articleNumber%%', description: 'Artikelnummer' },
      { key: '%%serialnr%%', description: 'Seriennummer' },
      { key: '%%techniciansKeys%%', description: 'Lieferant/Techniker-Kürzel' },
    ],
  },
}

// ─── API hooks ────────────────────────────────────────────────────────────────

const QUERY_KEY = ['asw-invoice-templates']
const BASE_URL = '/automatic-support-workflow/invoice-templates'

export function useInvoiceTemplates() {
  return useQuery<SupportWorkflowInvoiceTemplate[]>({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get(BASE_URL).then((r) => r.data),
  })
}

function useCreateInvoiceTemplate() {
  const qc = useQueryClient()
  return useMutation<SupportWorkflowInvoiceTemplate, Error, Omit<SupportWorkflowInvoiceTemplate, 'id' | 'isDefault' | 'createdAt' | 'updatedAt'>>({
    mutationFn: (body) => apiClient.post(BASE_URL, body).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

function useUpdateInvoiceTemplate() {
  const qc = useQueryClient()
  return useMutation<SupportWorkflowInvoiceTemplate, Error, { id: string } & Partial<Omit<SupportWorkflowInvoiceTemplate, 'id' | 'isDefault' | 'createdAt' | 'updatedAt'>>>({
    mutationFn: ({ id, ...body }) => apiClient.patch(`${BASE_URL}/${id}`, body).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

function useDeleteInvoiceTemplate() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiClient.delete(`${BASE_URL}/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

// ─── Form ─────────────────────────────────────────────────────────────────────

interface TplForm {
  name: string
  description: string
  content: string
  type: AswInvoiceTemplateType
  active: boolean
}

function defaultForm(): TplForm {
  return { name: '', description: '', content: '', type: 'ticket', active: true }
}

function formFromTemplate(t: SupportWorkflowInvoiceTemplate): TplForm {
  return {
    name: t.name,
    description: t.description ?? '',
    content: t.content,
    type: t.type,
    active: t.active,
  }
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface TplModalProps {
  open: boolean
  editTarget: SupportWorkflowInvoiceTemplate | null
  onClose: () => void
}

function TemplateModal({ open, editTarget, onClose }: TplModalProps) {
  const { toast } = useToast()
  const create = useCreateInvoiceTemplate()
  const update = useUpdateInvoiceTemplate()
  const [form, setForm] = useState<TplForm>(() => editTarget ? formFromTemplate(editTarget) : defaultForm())

  useEffect(() => {
    if (!open) return
    setForm(editTarget ? formFromTemplate(editTarget) : defaultForm())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function set<K extends keyof TplForm>(k: K, v: TplForm[K]) {
    setForm((p) => ({ ...p, [k]: v }))
  }

  function handleSubmit() {
    if (!form.name.trim()) { toast({ title: 'Name erforderlich', tone: 'danger' }); return }
    if (!form.content.trim()) { toast({ title: 'Vorlagentext erforderlich', tone: 'danger' }); return }
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      content: form.content.trim(),
      type: form.type,
      active: form.active,
    }
    if (editTarget) {
      update.mutate({ id: editTarget.id, ...payload }, {
        onSuccess: () => { toast({ title: 'Template aktualisiert', tone: 'success' }); onClose() },
        onError: (e) => toast({ title: 'Fehler', description: e.message, tone: 'danger' }),
      })
    } else {
      create.mutate(payload, {
        onSuccess: () => { toast({ title: 'Template angelegt', tone: 'success' }); onClose() },
        onError: (e) => toast({ title: 'Fehler', description: e.message, tone: 'danger' }),
      })
    }
  }

  const isPending = create.isPending || update.isPending
  const typeMeta = TEMPLATE_TYPE_META[form.type]

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={editTarget ? 'Template bearbeiten' : 'Neues Rechnungstemplate'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending}>
            {editTarget ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="z.B. Standard TANSS-Support"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ <span className="text-danger-500">*</span>
            </label>
            <SelectRoot
              value={form.type}
              onValueChange={(v) => set('type', v as AswInvoiceTemplateType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ticket">Servicebericht (Ticket)</SelectItem>
                <SelectItem value="service">Servicetextzeile</SelectItem>
                <SelectItem value="ware">Warentextzeile</SelectItem>
              </SelectContent>
            </SelectRoot>
          </div>
        </div>

        <Input
          label="Beschreibung"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Optional: Kurzbeschreibung des Templates"
        />

        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-1.5 text-blue-700">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-xs font-semibold">{typeMeta.description}</span>
          </div>
          <p className="text-xs font-medium text-blue-700 mb-1.5">Verfügbare Variablen:</p>
          <div className="flex flex-wrap gap-1.5">
            {typeMeta.variables.map((v) => (
              <button
                key={v.key}
                type="button"
                title={`${v.description} – Klicken zum Kopieren`}
                onClick={() => navigator.clipboard.writeText(v.key).catch(() => {})}
                className="flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-700 text-xs font-mono hover:bg-blue-100 transition-colors"
              >
                <Copy className="w-2.5 h-2.5 opacity-60" />
                {v.key}
              </button>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2">Klicken zum Kopieren. Variablen werden beim Versand automatisch ersetzt.</p>
        </div>

        <Textarea
          label="Vorlagentext"
          value={form.content}
          onChange={(e) => set('content', e.target.value)}
          placeholder="Vorlagentext mit TANSS-Variablen (%%variable%%) …"
          rows={8}
          hint="TANSS-Variablen werden beim Versand automatisch durch echte Werte ersetzt."
          required
        />
        <Switch
          label="Aktiv"
          checked={form.active}
          onChange={(e) => set('active', e.target.checked)}
        />
      </div>
    </Modal>
  )
}

// ─── Type badge helper ────────────────────────────────────────────────────────

const TYPE_BADGE_CLASS: Record<AswInvoiceTemplateType, string> = {
  ticket: 'bg-blue-100 text-blue-700',
  service: 'bg-green-100 text-green-700',
  ware: 'bg-amber-100 text-amber-700',
}

function TypeBadge({ type }: { type: AswInvoiceTemplateType }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE_CLASS[type]}`}>
      {TEMPLATE_TYPE_META[type]?.label ?? type}
    </span>
  )
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

export function InvoiceTemplatesTab() {
  const { toast } = useToast()
  const { data: templates = [], isLoading, error, refetch } = useInvoiceTemplates()
  const deleteTpl = useDeleteInvoiceTemplate()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<SupportWorkflowInvoiceTemplate | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SupportWorkflowInvoiceTemplate | null>(null)

  const columns: ColumnDef<SupportWorkflowInvoiceTemplate, unknown>[] = [
    {
      accessorKey: 'type',
      header: 'Typ',
      size: 160,
      cell: ({ row }) => <TypeBadge type={row.original.type} />,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-gray-900">{row.original.name}</p>
          {row.original.description && (
            <p className="text-xs text-gray-500 mt-0.5">{row.original.description}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'content',
      header: 'Vorlagentext (Vorschau)',
      cell: ({ getValue }) => (
        <span className="text-sm text-gray-500 truncate max-w-sm block font-mono">
          {(getValue() as string).slice(0, 60)}{(getValue() as string).length > 60 ? '…' : ''}
        </span>
      ),
    },
    {
      accessorKey: 'active',
      header: 'Status',
      size: 100,
      cell: ({ row }) => <StatusBadge status={row.original.active ? 'active' : 'inactive'} />,
    },
    {
      id: '__actions__',
      header: '',
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <IconButton
            icon={<Pencil className="w-3.5 h-3.5" />}
            aria-label="Template bearbeiten"
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setEditTarget(row.original); setModalOpen(true) }}
          />
          <IconButton
            icon={<Trash2 className="w-3.5 h-3.5" />}
            aria-label="Template löschen"
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original) }}
          />
        </div>
      ),
    },
  ]

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Templates konnten nicht geladen werden." onRetry={() => void refetch()} />

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          Wiederverwendbare Textvorlagen für TANSS-Rechnungen — je Typ (Servicebericht, Servicetextzeile, Warentextzeile) eine Vorlage definieren.
        </p>
        <Button
          variant="primary"
          size="sm"
          leadingIcon={<Plus className="w-4 h-4" />}
          onClick={() => { setEditTarget(null); setModalOpen(true) }}
        >
          Neues Template
        </Button>
      </div>

      <DataTable<SupportWorkflowInvoiceTemplate>
        data={templates}
        columns={columns}
        getRowId={(r) => r.id}
        searchPlaceholder="Templates suchen …"
        emptyState={
          <EmptyState
            title="Noch keine Rechnungstemplates"
            description="Lege je ein Template für Servicebericht, Servicetextzeile und Warentextzeile an."
            action={
              <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => { setEditTarget(null); setModalOpen(true) }}>
                Neues Template
              </Button>
            }
          />
        }
      />

      <TemplateModal
        open={modalOpen}
        editTarget={editTarget}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        title="Template löschen?"
        message={`"${deleteTarget?.name ?? ''}" wird unwiderruflich gelöscht.`}
        confirmLabel="Löschen"
        variant="danger"
        loading={deleteTpl.isPending}
        onConfirm={() => {
          if (!deleteTarget) return
          deleteTpl.mutate(deleteTarget.id, {
            onSuccess: () => { toast({ title: 'Template gelöscht', tone: 'success' }); setDeleteTarget(null) },
            onError: (e) => toast({ title: 'Fehler', description: e.message, tone: 'danger' }),
          })
        }}
      />
    </>
  )
}
